import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { FormEvent } from 'react'

import {
  Alert,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  Modal,
  SkeletonRows,
  TextField,
  mutedTextClass,
} from '@/components/layout/ui'
import { LabelChip, LabelSwatch, issueKeys, v2Api } from '@/features/issue'
import type { LabelView } from '@/features/issue/types'
import { cn } from '@/lib/cn'
import { fieldErrorFor, messageFor } from '@/lib/errors'
import { useWorkspace } from '@/lib/workspace'

/**
 * `/{slug}/settings/labels` — the workspace's label registry.
 *
 * Labels could already be created while writing an issue: typing a new name in
 * the label picker creates it. That is the right thing on the create path, but
 * it left no way to see what exists, fix a typo, recolour anything, or delete a
 * label once it had been made — the registry grew and could never be tidied.
 *
 * Deleting is the one destructive action here, so the count of issues currently
 * carrying the label is shown in the confirm dialog. Removing a label detaches
 * it from those issues and does not touch the issues themselves; saying so
 * before the click is the difference between a considered action and a scare.
 */

/** The colour tokens the API accepts. An unknown one is rejected server-side. */
const LABEL_COLORS = [
  'slate',
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'teal',
  'cyan',
  'blue',
  'indigo',
  'violet',
  'purple',
  'pink',
  'rose',
] as const

const MAX_NAME_LENGTH = 64

function ColorPicker({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (color: string) => void
  disabled?: boolean
}) {
  return (
    <div role="radiogroup" aria-label="Label colour" className="flex flex-wrap gap-1.5">
      {LABEL_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          role="radio"
          aria-checked={color === value}
          aria-label={color}
          title={color}
          disabled={disabled}
          onClick={() => onChange(color)}
          className={cn(
            'flex size-7 items-center justify-center rounded-full transition',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
            'disabled:cursor-not-allowed disabled:opacity-50',
            color === value
              ? 'ring-2 ring-ink-900 ring-offset-2 dark:ring-ink-100'
              : 'ring-1 ring-ink-200 hover:ring-ink-400 dark:ring-ink-700',
          )}
        >
          <LabelSwatch color={color} />
        </button>
      ))}
    </div>
  )
}

export default function LabelsPage() {
  const workspace = useWorkspace()
  const slug = workspace.slug
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [color, setColor] = useState<string>('slate')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<LabelView | null>(null)
  const [deleting, setDeleting] = useState<LabelView | null>(null)

  const labels = useQuery({
    queryKey: issueKeys.labels(slug),
    queryFn: () => v2Api.labels(slug),
    select: (response) => response.data,
  })

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: issueKeys.labels(slug) })

  const create = useMutation({
    mutationFn: () => v2Api.createLabel(slug, { name: name.trim(), color }),
    onSuccess: async () => {
      setName('')
      setColor('slate')
      await invalidate()
    },
  })

  const update = useMutation({
    mutationFn: (input: { id: string; name: string; color: string }) =>
      v2Api.updateLabel(slug, input.id, { name: input.name.trim(), color: input.color }),
    onSuccess: async () => {
      setEditing(null)
      await invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => v2Api.deleteLabel(slug, id),
    onSuccess: async () => {
      setDeleting(null)
      await invalidate()
    },
  })

  const rows = useMemo(() => {
    const all = labels.data ?? []
    const term = search.trim().toLowerCase()
    if (!term) return all
    return all.filter((label) => label.name.toLowerCase().includes(term))
  }, [labels.data, search])

  const onCreate = (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim() || create.isPending) return
    create.mutate()
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Labels"
          description="Shared across every project in this workspace. Labels can also be created while writing an issue; this is where they are renamed, recoloured and removed."
        />

        <form onSubmit={onCreate} className="space-y-4 p-5">
          {create.isError && !fieldErrorFor(create.error, 'name') && (
            <Alert tone="danger">{messageFor(create.error)}</Alert>
          )}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="min-w-0 flex-1">
              <TextField
                label="New label"
                value={name}
                maxLength={MAX_NAME_LENGTH}
                placeholder="backend"
                onChange={(event) => setName(event.target.value)}
                error={fieldErrorFor(create.error, 'name')}
                hint={`Up to ${MAX_NAME_LENGTH} characters.`}
              />
            </div>
            <div className="sm:pt-1.5">
              <span className="mb-1.5 block text-sm font-medium">Colour</span>
              <ColorPicker value={color} onChange={setColor} disabled={create.isPending} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button type="submit" variant="primary" disabled={!name.trim() || create.isPending}>
              {create.isPending ? 'Adding…' : 'Add label'}
            </Button>
            {name.trim() && (
              <span className="flex items-center gap-2 text-sm">
                <span className={mutedTextClass}>Preview</span>
                <LabelChip
                  label={{ id: 'preview', organization_id: '', name: name.trim(), color }}
                />
              </span>
            )}
          </div>
        </form>
      </Card>

      <Card>
        <CardHeader
          title={`In this workspace${labels.data ? ` (${labels.data.length})` : ''}`}
          description="The number beside each label is how many issues currently carry it."
          action={
            (labels.data?.length ?? 0) > 0 ? (
              <div className="w-56">
                <TextField
                  label="Search labels"
                  hideLabel
                  value={search}
                  placeholder="Search labels"
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            ) : undefined
          }
        />

        {labels.isPending ? (
          <div className="p-5">
            <SkeletonRows rows={4} />
          </div>
        ) : labels.isError ? (
          <div className="p-5">
            <Alert tone="danger">{messageFor(labels.error)}</Alert>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title={search.trim() ? 'No labels match that search' : 'No labels yet'}
            description={
              search.trim()
                ? 'Try a different search.'
                : 'Add one above, or create labels as you write issues.'
            }
          />
        ) : (
          <ul className="divide-y divide-ink-200 dark:divide-ink-800">
            {rows.map((label) => (
              <li key={label.id} className="flex items-center gap-3 px-5 py-3.5">
                <div className="min-w-0 flex-1">
                  <LabelChip label={label} />
                </div>
                <span className={cn('tabular-nums text-sm', mutedTextClass)}>
                  {label.issue_count} {label.issue_count === 1 ? 'issue' : 'issues'}
                </span>
                <Button
                  variant="ghost"
                  onClick={() => setEditing(label)}
                  aria-label={`Edit label ${label.name}`}
                  title="Edit"
                >
                  <Pencil className="size-4" aria-hidden="true" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setDeleting(label)}
                  aria-label={`Delete label ${label.name}`}
                  title="Delete"
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <EditLabelModal
        label={editing}
        onClose={() => setEditing(null)}
        onSave={(next) => update.mutate(next)}
        pending={update.isPending}
        error={update.error}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        loading={remove.isPending}
        error={remove.isError ? messageFor(remove.error) : undefined}
        title={`Delete ${deleting?.name ?? 'label'}?`}
        // The count is the whole point of this sentence: it is the difference
        // between deleting a label nobody uses and one that is on 40 issues.
        description={
          deleting && deleting.issue_count > 0
            ? `This removes the label from ${deleting.issue_count} ${
                deleting.issue_count === 1 ? 'issue' : 'issues'
              }. The issues themselves are not deleted.`
            : 'This label is not on any issue.'
        }
        confirmLabel="Delete label"
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id)
        }}
        onClose={() => setDeleting(null)}
      />
    </div>
  )
}

function EditLabelModal({
  label,
  onClose,
  onSave,
  pending,
  error,
}: {
  label: LabelView | null
  onClose: () => void
  onSave: (input: { id: string; name: string; color: string }) => void
  pending: boolean
  error: unknown
}) {
  // Keyed on the label id so opening a different row resets the draft, without
  // an effect that would fight the user's typing.
  return label ? (
    <EditLabelForm
      key={label.id}
      label={label}
      onClose={onClose}
      onSave={onSave}
      pending={pending}
      error={error}
    />
  ) : null
}

function EditLabelForm({
  label,
  onClose,
  onSave,
  pending,
  error,
}: {
  label: LabelView
  onClose: () => void
  onSave: (input: { id: string; name: string; color: string }) => void
  pending: boolean
  error: unknown
}) {
  const [name, setName] = useState(label.name)
  const [color, setColor] = useState(label.color)

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit label"
      description="Renaming a label updates it everywhere it is used."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || pending}
            onClick={() => onSave({ id: label.id, name, color })}
          >
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error != null && !fieldErrorFor(error, 'name') && (
          <Alert tone="danger">{messageFor(error)}</Alert>
        )}
        <TextField
          label="Name"
          value={name}
          maxLength={MAX_NAME_LENGTH}
          onChange={(event) => setName(event.target.value)}
          error={fieldErrorFor(error, 'name')}
        />
        <div>
          <span className="mb-1.5 block text-sm font-medium">Colour</span>
          <ColorPicker value={color} onChange={setColor} disabled={pending} />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className={mutedTextClass}>Preview</span>
          <LabelChip
            label={{ ...label, name: name.trim() || label.name, color }}
          />
        </div>
      </div>
    </Modal>
  )
}
